<template>
  <div class="rp-root">
    <button class="rp-theme-toggle" @click="toggleTheme" :title="darkMode?'Switch to Light':'Switch to Dark'">
      <i :class="darkMode?'mdi mdi-white-balance-sunny':'mdi mdi-moon-waning-crescent'"></i>
    </button>

    <div class="rp-wrapper">
      <div class="rp-card">
        <!-- Logo -->
        <div class="text-center mb-4">
          <img
            :src="$brand.logo"
            alt="Hospital Management System"
            class="rp-logo"
            @click="$router.push('/')"
          />
          <p class="rp-subtitle mt-2">Reset your account password</p>
        </div>

        <!-- Stepper -->
        <div v-if="step < 3" class="rp-stepper mb-4">
          <div class="rp-step" :class="{ active: step === 1, done: step > 1 }">
            <div class="rp-step-num"><i v-if="step > 1" class="mdi mdi-check"></i><span v-else>1</span></div>
            <div class="rp-step-label">Email</div>
          </div>
          <div class="rp-step-line" :class="{ done: step > 1 }"></div>
          <div class="rp-step" :class="{ active: step === 2 }">
            <div class="rp-step-num"><span>2</span></div>
            <div class="rp-step-label">Verify &amp; Reset</div>
          </div>
        </div>

        <!-- ===== STEP 1: REQUEST RESET CODE ===== -->
        <div v-if="step === 1">
          <h5 class="text-center fw-bold mb-2 rp-title">Forgot Your Password?</h5>
          <p class="text-center rp-desc mb-4">Enter your account email and we'll send you a 6-digit reset code.</p>

          <div class="mb-3">
            <label class="auth-label">Email Address</label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-email-outline"></i></span>
              <input
                v-model="form.email"
                type="email"
                class="form-control auth-input"
                placeholder="you@yourhospital.com"
                autocomplete="email"
                @keyup.enter="requestCode"
              />
            </div>
          </div>

          <div v-if="errorMsg" class="alert rp-alert-danger py-2 px-3">
            <i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}
          </div>

          <button
            class="btn btn-primary w-100 mt-2"
            @click="requestCode"
            :disabled="!form.email || submitting"
          >
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Sending code...
            </span>
            <span v-else>
              <i class="mdi mdi-email-fast me-1"></i> Send Reset Code
            </span>
          </button>

          <div class="text-center mt-4 rp-secondary-text">
            Remembered it?
            <a href="javascript:void(0)" @click="$router.push('/login')" class="rp-link fw-semibold">Sign in</a>
          </div>
        </div>

        <!-- ===== STEP 2: ENTER CODE + NEW PASSWORD ===== -->
        <div v-if="step === 2">
          <div class="text-center mb-3">
            <div class="rp-mail-icon">
              <i class="mdi mdi-email-check"></i>
            </div>
            <h5 class="fw-bold rp-title">Check Your Email</h5>
            <p class="rp-desc">
              We sent a 6-digit code to <strong>{{ form.email }}</strong>.
              Enter it below along with your new password.
            </p>
          </div>

          <div class="mb-3">
            <label class="auth-label">6-Digit Code</label>
            <input
              v-model="form.code"
              type="text"
              class="form-control auth-input rp-code-input"
              placeholder="000000"
              maxlength="6"
              inputmode="numeric"
              autocomplete="one-time-code"
              @input="form.code = form.code.replace(/[^0-9]/g, '')"
            />
          </div>

          <div class="mb-3">
            <label class="auth-label">New Password</label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-lock"></i></span>
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="form.newPassword"
                class="form-control auth-input"
                placeholder="Min 8 characters"
                autocomplete="new-password"
              />
              <button type="button" class="input-group-text password-toggle" @click="showPassword = !showPassword">
                <i class="mdi" :class="showPassword ? 'mdi-eye-off' : 'mdi-eye'"></i>
              </button>
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Confirm Password</label>
            <div class="input-group">
              <span class="input-group-text auth-input-icon"><i class="mdi mdi-lock-check"></i></span>
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="form.confirmPassword"
                class="form-control auth-input"
                placeholder="Re-enter password"
                autocomplete="new-password"
                @keyup.enter="resetPassword"
              />
            </div>
            <div v-if="form.confirmPassword && form.newPassword !== form.confirmPassword" class="auth-hint auth-hint-error">
              <i class="mdi mdi-alert-circle me-1"></i>Passwords don't match
            </div>
          </div>

          <div v-if="errorMsg" class="alert rp-alert-danger py-2 px-3">
            <i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}
          </div>

          <div class="d-flex gap-2 mt-2">
            <button class="btn btn-outline-secondary" @click="step = 1; errorMsg = ''" :disabled="submitting">
              <i class="mdi mdi-arrow-left me-1"></i> Back
            </button>
            <button
              class="btn btn-primary flex-grow-1"
              @click="resetPassword"
              :disabled="!canReset || submitting"
            >
              <span v-if="submitting">
                <span class="spinner-border spinner-border-sm me-1"></span> Resetting...
              </span>
              <span v-else>
                <i class="mdi mdi-lock-reset me-1"></i> Reset Password
              </span>
            </button>
          </div>

          <div class="text-center mt-3">
            <a href="javascript:void(0)" @click="resendCode" class="rp-link rp-link-sm" :class="{ disabled: resendCooldown > 0 }">
              {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code' }}
            </a>
          </div>
        </div>

        <!-- ===== STEP 3: SUCCESS ===== -->
        <div v-if="step === 3">
          <div class="text-center">
            <div class="rp-success-icon">
              <i class="mdi mdi-check-circle"></i>
            </div>
            <h5 class="fw-bold rp-title">Password Reset</h5>
            <p class="rp-success-sub">
              Your password has been updated successfully.<br>
              You can now sign in with your new password.
            </p>
            <button class="btn btn-primary w-100 mt-3" @click="$router.push('/login')">
              <i class="mdi mdi-login me-1"></i> Sign In Now
            </button>
            <button class="btn btn-link w-100 mt-2 rp-secondary-text" @click="$router.push('/')">
              Back to home
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:          #f0f0f0;
  --hms-s:          #a0a0a0;
  --hms-m:          #808080;
  --hms-bg1:        #141414;
  --hms-bg2:        #0a0a0a;
  --hms-bg3:        #1e1e1e;
  --hms-bg4:        #0f0f0f;
  --hms-br:         #2a2a2a;
  --hms-input-br:   #333333;
  --hms-placeholder:#4a4a4a;
  --hms-primary:    #1a7fe8;
  --hms-primary-lt: rgba(26,127,232,0.12);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
  --hms-alert-bg:   #2d1111;
  --hms-alert-br:   #5c2020;
  --hms-alert-text: #ff6b6b;
}
html[data-theme="light"] {
  --hms-h:          #1a1a1a;
  --hms-s:          #555555;
  --hms-m:          #777777;
  --hms-bg1:        #ffffff;
  --hms-bg2:        #f4f6f9;
  --hms-bg3:        #eef1f5;
  --hms-bg4:        #ffffff;
  --hms-br:         #dee2e8;
  --hms-input-br:   #ced4da;
  --hms-placeholder:#aaaaaa;
  --hms-primary:    #0047AB;
  --hms-primary-lt: rgba(0,71,171,0.08);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
  --hms-alert-bg:   #fff1f1;
  --hms-alert-br:   #f1b5b5;
  --hms-alert-text: #c43d3d;
}
html[data-theme="light"] body,
html[data-theme="dark"] body {
  background: var(--hms-bg2) !important;
  color: var(--hms-h) !important;
}
</style>

<style scoped>
.rp-root { background: var(--hms-bg2) !important; min-height: 100vh; position: relative; }
.rp-theme-toggle { position: fixed; top: 16px; right: 16px; z-index: 10; width: 40px; height: 40px; border-radius: 50%; background: var(--hms-bg3); border: 1px solid var(--hms-br); color: var(--hms-primary); cursor: pointer; font-size: 18px; display: flex; align-items: center; justify-content: center; transition: all .2s; }
.rp-theme-toggle:hover { transform: scale(1.05); box-shadow: 0 4px 12px var(--hms-primary-lt); }

.rp-wrapper { display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; }
.rp-card { background: var(--hms-bg1) !important; border: 1px solid var(--hms-br); border-radius: 16px; padding: 40px 32px; width: 100%; max-width: 460px; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06); }
.rp-logo { height: 52px; cursor: pointer; }
.rp-subtitle { color: var(--hms-s); font-size: 13px; }
.rp-title { color: var(--hms-h); }
.rp-desc { color: var(--hms-m); font-size: 13px; line-height: 1.6; }

/* Stepper */
.rp-stepper { display: flex; align-items: center; justify-content: center; }
.rp-step { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.rp-step-num { width: 32px; height: 32px; border-radius: 50%; background: var(--hms-bg3); border: 2px solid var(--hms-br); color: var(--hms-s); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; transition: all .2s; }
.rp-step.active .rp-step-num { background: var(--hms-primary); border-color: var(--hms-primary); color: #fff; }
.rp-step.done .rp-step-num { background: var(--hms-success); border-color: var(--hms-success); color: #fff; }
.rp-step-label { font-size: 11px; font-weight: 600; color: var(--hms-s); text-transform: uppercase; letter-spacing: 0.5px; }
.rp-step.active .rp-step-label { color: var(--hms-primary); }
.rp-step-line { flex: 0 0 60px; height: 2px; background: var(--hms-br); margin: 0 12px 22px; }
.rp-step-line.done { background: var(--hms-success); }

.auth-label { font-size: 13px; font-weight: 600; color: var(--hms-m) !important; margin-bottom: 6px; display: block; }
.auth-input, .auth-input-icon, .password-toggle { background: var(--hms-bg4) !important; border-color: var(--hms-input-br) !important; color: var(--hms-h) !important; }
.auth-input-icon { color: var(--hms-s) !important; }
.auth-input:focus, .password-toggle:focus { border-color: var(--hms-primary) !important; box-shadow: 0 0 0 2px var(--hms-primary-lt) !important; outline: none; }
.auth-input { border-radius: 8px; }
.auth-input::placeholder { color: var(--hms-placeholder) !important; }
.password-toggle { cursor: pointer; }
.auth-hint { font-size: 11px; color: var(--hms-s); margin-top: 4px; }
.auth-hint-error { color: var(--hms-danger); }

.rp-code-input { font-size: 24px !important; letter-spacing: 8px; font-weight: 700; text-align: center; }

.rp-link { color: var(--hms-primary); text-decoration: none; }
.rp-link:hover { text-decoration: underline; }
.rp-link-sm { font-size: 12px; }
.rp-link.disabled { color: var(--hms-s); pointer-events: none; }

.rp-alert-danger { background: var(--hms-alert-bg); border: 1px solid var(--hms-alert-br); color: var(--hms-alert-text); font-size: 13px; }
.rp-secondary-text { color: var(--hms-s); font-size: 13px; }

.rp-mail-icon { width: 64px; height: 64px; border-radius: 50%; background: var(--hms-primary-lt); display: inline-flex; align-items: center; justify-content: center; margin-bottom: 12px; }
.rp-mail-icon i { font-size: 30px; color: var(--hms-primary); }

.rp-success-icon { width: 72px; height: 72px; border-radius: 50%; background: rgba(40, 167, 69, 0.12); display: inline-flex; align-items: center; justify-content: center; margin-bottom: 16px; }
.rp-success-icon i { font-size: 36px; color: var(--hms-success); }
.rp-success-sub { color: var(--hms-m); font-size: 13px; line-height: 1.6; }

@media (max-width: 480px) {
  .rp-card { padding: 28px 20px; border-radius: 12px; }
  .rp-step-line { flex: 0 0 32px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, hms } from "@/api";

export default {
  name: "ResetPasswordPage",
  data() {
    return {
      darkMode: (localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light") !== "light",
      themeObserver: null,
      step: 1,
      submitting: false,
      showPassword: false,
      errorMsg: "",
      resendCooldown: 0,
      resendTimer: null,
      form: {
        email: "",
        code: "",
        newPassword: "",
        confirmPassword: ""
      }
    };
  },
  computed: {
    canReset() {
      return (
        this.form.code.length === 6 &&
        this.form.newPassword.length >= 8 &&
        this.form.newPassword === this.form.confirmPassword
      );
    }
  },
  mounted() {
    this.applyTheme();
    this.watchTheme();
    if (this.$route.query.email) {
      this.form.email = this.$route.query.email;
    }
  },
  beforeUnmount() {
    if (this.themeObserver) this.themeObserver.disconnect();
    if (this.resendTimer) clearInterval(this.resendTimer);
    window.removeEventListener("storage", this.applyTheme);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
      this.darkMode = t !== "light";
    },
    toggleTheme() {
      this.darkMode = !this.darkMode;
      var theme = this.darkMode ? "dark" : "light";
      localStorage.setItem("hms_theme", theme);
      document.documentElement.setAttribute("data-theme", theme);
    },
    watchTheme() {
      var self = this;
      this.themeObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(m) {
          if (m.type === "attributes" && m.attributeName === "data-theme") {
            self.darkMode = document.documentElement.getAttribute("data-theme") !== "light";
          }
        });
      });
      this.themeObserver.observe(document.documentElement, { attributes: true });
      window.addEventListener("storage", this.applyTheme);
    },
    isValidEmail(s) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s || ""); },
    startResendCooldown() {
      this.resendCooldown = 60;
      if (this.resendTimer) clearInterval(this.resendTimer);
      var self = this;
      this.resendTimer = setInterval(function() {
        self.resendCooldown--;
        if (self.resendCooldown <= 0) {
          clearInterval(self.resendTimer);
          self.resendTimer = null;
        }
      }, 1000);
    },
    async requestCode() {
      this.errorMsg = "";
      if (!this.isValidEmail(this.form.email)) {
        this.errorMsg = "Please enter a valid email address.";
        return;
      }
      this.submitting = true;
      try {
        var r = await executeJson(
          { function: "requestPasswordReset", email: this.form.email.trim() },
          hms
        );
        if (r.data.success) {
          this.step = 2;
          this.startResendCooldown();
        } else {
          this.errorMsg = r.data.message || "Could not send reset code. Please try again.";
        }
      } catch (e) {
        console.error(e);
        this.errorMsg = "Connection error. Please try again.";
      }
      this.submitting = false;
    },
    async resendCode() {
      if (this.resendCooldown > 0) return;
      await this.requestCode();
    },
    async resetPassword() {
      this.errorMsg = "";
      if (this.form.code.length !== 6) {
        this.errorMsg = "Enter the 6-digit code sent to your email.";
        return;
      }
      if (this.form.newPassword.length < 8) {
        this.errorMsg = "Password must be at least 8 characters.";
        return;
      }
      if (this.form.newPassword !== this.form.confirmPassword) {
        this.errorMsg = "Passwords don't match.";
        return;
      }
      this.submitting = true;
      try {
        var r = await executeJson(
          {
            function:     "resetPasswordWithToken",
            email:        this.form.email.trim(),
            code:         this.form.code,
            new_password: this.form.newPassword
          },
          hms
        );
        if (r.data.success) {
          this.step = 3;
        } else {
          this.errorMsg = r.data.message || "Could not reset password. Please check the code and try again.";
        }
      } catch (e) {
        console.error(e);
        this.errorMsg = "Connection error. Please try again.";
      }
      this.submitting = false;
    }
  }
};
</script>
